import React, { useState, useEffect } from 'react';
import {
  Monitor,
  Smartphone,
  Globe,
  Tv,
  Trash2,
  Shield,
  Clock,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Loader,
  RefreshCw
} from 'lucide-react';
import { apiClient, SessionInfo } from '../utils/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const DeviceIcon: React.FC<{ deviceType: string; className?: string }> = ({ deviceType, className = "w-6 h-6" }) => {
  switch (deviceType) {
    case 'ios':
    case 'android':
      return <Smartphone className={className} />;
    case 'kiosk':
      return <Tv className={className} />;
    case 'web':
    default:
      return <Globe className={className} />;
  }
};

const getDeviceLabel = (deviceType: string): string => {
  switch (deviceType) {
    case 'ios': return 'iOS App';
    case 'android': return 'Android App';
    case 'kiosk': return 'Kiosk Display';
    case 'web': return 'Web Browser';
    default: return deviceType;
  }
};

const parseUserAgent = (ua?: string): string => {
  if (!ua) return 'Unknown device';

  // Extract browser/app info
  if (ua.includes('HousePoints')) return 'HousePoints iOS App';
  if (ua.includes('Chrome')) return 'Chrome Browser';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari Browser';
  if (ua.includes('Firefox')) return 'Firefox Browser';
  if (ua.includes('Edge')) return 'Edge Browser';

  return 'Unknown Browser';
};

const getInitials = (name?: string): string => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Color palette for different users
const userColors = [
  { bg: 'bg-blue-50', border: 'border-blue-200', avatar: 'bg-blue-600' },
  { bg: 'bg-purple-50', border: 'border-purple-200', avatar: 'bg-purple-600' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', avatar: 'bg-emerald-600' },
  { bg: 'bg-orange-50', border: 'border-orange-200', avatar: 'bg-orange-600' },
  { bg: 'bg-pink-50', border: 'border-pink-200', avatar: 'bg-pink-600' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', avatar: 'bg-cyan-600' },
];

/**
 * SessionsView - Embedded version for Settings page
 * Shows active sessions with user avatars and colored grouping
 */
export const SessionsView: React.FC = () => {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSessions();
      setSessions(data.sessions || []);
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevokeSession = async (sessionId: string, isCurrent: boolean) => {
    if (isCurrent) {
      toast.error('You cannot revoke your current session');
      return;
    }

    try {
      setRevoking(sessionId);
      await apiClient.revokeSession(sessionId);
      toast.success('Session revoked');
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err: any) {
      console.error('Failed to revoke session:', err);
      toast.error('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    const otherSessions = sessions.filter(s => !s.is_current);
    if (otherSessions.length === 0) {
      toast.error('No other sessions to revoke');
      return;
    }

    if (!window.confirm(`Are you sure you want to revoke ${otherSessions.length} other session(s)? This will log out all other devices.`)) {
      return;
    }

    try {
      setRevokingAll(true);
      await apiClient.revokeAllOtherSessions();
      toast.success('All other sessions revoked');
      setSessions(prev => prev.filter(s => s.is_current));
    } catch (err: any) {
      console.error('Failed to revoke sessions:', err);
      toast.error('Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  // Group sessions by user
  const sessionsByUser = sessions.reduce((acc, session) => {
    const key = session.username || session.user_id;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(session);
    return acc;
  }, {} as Record<string, SessionInfo[]>);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-3 rounded-xl">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Active Sessions</h2>
            <p className="text-gray-500">
              Manage your family's logged-in devices
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchSessions}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleRevokeAllOthers}
            disabled={revokingAll || sessions.filter(s => !s.is_current).length === 0}
            className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {revokingAll ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
            Revoke All Others
          </button>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No active sessions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(sessionsByUser).map(([username, userSessions], userIndex) => {
            const colors = userColors[userIndex % userColors.length];
            return (
              <div key={username} className={`rounded-xl shadow-sm overflow-hidden border ${colors.bg} ${colors.border}`}>
                <div className={`px-4 py-3 border-b ${colors.border}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${colors.avatar} flex items-center justify-center text-white font-medium`}>
                      {getInitials(username)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{username}</h3>
                      <p className="text-sm text-gray-500">
                        {userSessions.length} active session{userSessions.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-gray-200/50">
                  {userSessions.map(session => (
                    <div
                      key={session.id}
                      className={`px-4 py-3 flex items-center justify-between ${
                        session.is_current ? 'bg-green-50/50' : 'bg-white/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* User avatar on each session */}
                        <div className={`w-8 h-8 rounded-full ${colors.avatar} flex items-center justify-center text-white text-sm font-medium flex-shrink-0`}>
                          {getInitials(username)}
                        </div>
                        <div className={`p-2 rounded-lg ${
                          session.is_current
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <DeviceIcon deviceType={session.device_type} className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {getDeviceLabel(session.device_type)}
                            </span>
                            {session.is_current && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {parseUserAgent(session.user_agent)}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                            {session.ip_address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {session.ip_address}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Active {formatDistanceToNow(new Date(session.last_active_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                      {!session.is_current && (
                        <button
                          onClick={() => handleRevokeSession(session.id, session.is_current)}
                          disabled={revoking === session.id}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Revoke session"
                        >
                          {revoking === session.id ? (
                            <Loader className="w-5 h-5 animate-spin" />
                          ) : (
                            <Trash2 className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">About Sessions</p>
            <p className="text-blue-600">
              Sessions are created when you log in from a device. If you see a session you don't recognize,
              revoke it immediately and consider changing your password.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsView;
